import React, { useEffect, useState, useRef, useCallback } from 'react';
import { History } from 'history';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { bindActionCreators, Dispatch } from 'redux';

import { Config } from 'utils/config/config';
import { routes } from 'utils/routeHelper';
import { loadAuthToken, loadSessionId } from 'utils/tokenStorage';
import Program from 'utils/program';
import { iframePostMessage } from 'utils/constants';
import { clearCurrentPatient } from 'actions/currentPatient';
import ScrollToTop from 'components/common/ScrollToTop';

import './styles.scss';
import { handleExpiredToken } from 'utils/session';

interface IProps {
  currentPatient: Nullable<CurrentPatientState>;
  clearCurrentPatient: () => void;
  history: History;
}

const PatientDashboardMFE = (props: IProps) => {
  const { currentPatient } = props;
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [loadedIframe, setLoadedIframe] = useState<Boolean>(false);
  const [iframe, setIframe] = useState<Window | null>(null);

  const onIframeLoaded = () => {
    let iframeWindow: Window | null;
    if (!iframe) {
      iframeWindow = iframeRef.current?.contentWindow || null;
      setIframe(iframeWindow);
      setLoadedIframe(true);
    }
  };

  const handleIframeMessage = useCallback((event: any) => {
    const { payload } = event.data;
    if (payload && payload.action === iframePostMessage.EXPIRED_TOKEN) {
      if (window.location.pathname !== routes.LOG_IN) {
        handleExpiredToken();
      }
    }
  }, []);

  useEffect(() => {
    if (currentPatient && iframe) {
      const data = {
        sessionId: loadSessionId(),
        authToken: loadAuthToken(),
        patientId: currentPatient.id,
        program: currentPatient.currentAccessRight.program,
      };
      iframe?.postMessage(data, Config.HYLIA_WEB_URL);
    }
  }, [currentPatient, iframe]);

  useEffect(() => {
    return () => {
      props.clearCurrentPatient();
      Program.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleIframeMessage);

    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [handleIframeMessage]);

  if (!currentPatient) return <Redirect to={routes.PATIENT_SUMMARY} />;
  return (
    <div
      data-testid={
        !loadedIframe
          ? 'patient-dashboard-iframe'
          : 'patient-dashboard-loaded-iframe'
      }
      styleName="wrapper"
    >
      <ScrollToTop>
        {Config.HYLIA_WEB_URL ? (
          <iframe
            title="patient-dashboard-iframe"
            ref={iframeRef}
            src={`${Config.HYLIA_WEB_URL}/patient-dashboard`}
            styleName="iframe-wrapper"
            onLoad={onIframeLoaded}
          />
        ) : (
          <div>Please define HYLIA_WEB_URL</div>
        )}
      </ScrollToTop>
    </div>
  );
};

const mapStateToProps = (state: IRootStateType) => ({
  currentPatient: state.currentPatient,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      clearCurrentPatient,
    },
    dispatch,
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PatientDashboardMFE);
